<script setup lang="ts">
/**
 * 審査履歴モーダル。
 * 従来は右ペインの独立カードだったが、常時表示しておく必要性が低く
 * 縦スペースを占有していたため、ヘッダーのリンクから開くモーダルに変更した。
 * 中身のロジック(取得・整形)は旧 JudgeLogPanel.vue と同じ。
 */
import { computed, ref } from 'vue'
import { useSelectionStore } from '@/stores/selection'
import { useJudgeLog } from '@/api/queries'

const emit = defineEmits<{ close: [] }>()

const selection = useSelectionStore()
const asinRef = computed(() => selection.selectedAsin)
// モーダルは開いた時点で常に「表示中」なのでtrue固定でよい
// (useJudgeLogは第2引数がtrueの間だけ取得する)
const expanded = ref(true)
const { data, isLoading, isError } = useJudgeLog(asinRef, expanded)

const logs = computed(() => data.value?.logs ?? [])

const resultLabel: Record<string, string> = {
  '1': '審査中',
  '2': '審査OK',
  '8': '出品NG',
  '9': '審査NG',
}

function resultClass(result: string): string {
  if (result === '2') return 'ok'
  if (result === '9') return 'ng'
  if (result === '8') return 'lng'
  return 'exam'
}

function fmt(value: string | null): string {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}
</script>

<template>
  <div class="bim-overlay" @click="emit('close')">
    <div class="bim-modal judgelog-modal" @click.stop>
      <div class="bim-head">
        <div class="bim-title">審査履歴</div>
        <button class="bim-close" @click="emit('close')">
          <svg class="icon" viewBox="0 0 24 24" style="width:18px;height:18px;"><path d="M18 6L6 18M6 6l12 12" /></svg>
        </button>
      </div>

      <div class="judgelog-modal-body">
        <div v-if="isLoading" class="empty-state" style="padding:14px 0;"><span class="spinner"></span> 読み込み中…</div>
        <div v-else-if="isError" class="empty-state" style="padding:14px 0;">履歴の取得に失敗しました。</div>
        <div v-else-if="logs.length === 0" class="empty-state" style="padding:14px 0;">審査履歴はありません</div>
        <div v-else class="judgelog-list">
          <div class="judgelog-row" v-for="(log, i) in logs" :key="i">
            <span class="judgelog-badge" :class="resultClass(log.JudeResult)">
              {{ resultLabel[log.JudeResult] ?? log.JudeResult }}
            </span>
            <span class="judgelog-date">{{ fmt(log.UpdateDate ?? log.CreateDate) }}</span>
            <!-- 審査者は CreateUser から引いた名前。UpdateUser は別の処理で上書きされるので使わない -->
            <span class="judgelog-user" :title="log.judgeUserRaw ?? log.CreateUser ?? ''">{{ log.judgeUserName ?? log.CreateUser ?? '-' }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
